/**
 * The shop look named by the address bar, for the screens that run before anybody is signed in.
 *
 * The dashboard is deployed one subdomain per shop — `oritech.example.com`, `diamond.example.com`
 * — so the sign-in screen can wear the right look on a device that has never signed in, which the
 * session hint cannot do. After sign-in the session decides, as it always has: this only seeds
 * the one moment the app would otherwise have to guess.
 *
 * The POS is deliberately *not* deployed this way. It is one till at `pos.example.com` serving
 * every shop, its sign-in belongs to nobody, and `pos` matches no theme code — so this returns
 * null there and the neutral look stands.
 *
 * **Display only**, exactly as the session hint is. A hostname is not a claim about who you are;
 * spoofing one gets somebody a differently coloured login form and nothing else.
 *
 * Locally: every `*.localhost` name resolves to the loopback address in Chrome, Firefox and
 * Safari with no `/etc/hosts` entry and no Vite config, so `http://diamond.localhost:5174`
 * exercises this against the running dev server exactly as a real subdomain would.
 */

import { shopThemes, type ShopThemeCode } from '@pos/tokens';

/**
 * The first DNS label, matched against the curated theme codes.
 *
 * Validated rather than trusted: an unmatched label returns null and the caller paints neutral.
 * Passing the label straight through would mean `www` or a typo'd subdomain setting
 * `data-shop-theme="www"`, which no stylesheet defines — and an undefined value falls through to
 * `:root`, which is OriTech. Every unknown host has to land on neutral, not on the first shop.
 */
export function shopThemeFromHost(hostname: string): ShopThemeCode | null {
  const label = hostname.split(':')[0]?.split('.')[0]?.toLowerCase() ?? '';
  const match = shopThemes.find((theme) => theme.code === label);

  return match?.code ?? null;
}
